import React, { useState, useRef } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Grid,
  MenuItem,
  Box,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PreviewIcon from '@mui/icons-material/Preview';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import axios from 'axios';
import LabelPreview from './components/LabelPreview';
import CarrierSelectionDialog from './components/CarrierSelectionDialog';
import { useRealisticLabelPDF } from './components/LabelToPDF';

const carriers = ['DHL', 'UPS', 'FedEx', 'USPS'];
const serviceTypes = ['Express', 'Standard', 'Economy'];

// Expanded name lists (English + Spanish)
const firstNames = [
  // English
  'John', 'Jane', 'Michael', 'Sarah', 'David', 'Emma', 'James', 'Lisa', 'Robert', 'Mary',
  // Spanish (no accents)
  'Juan', 'Maria', 'Jose', 'Carmen', 'Luis', 'Ana', 'Miguel', 'Lucia', 'Pedro', 'Sofia',
  'Carlos', 'Isabella', 'Javier', 'Valentina', 'Diego', 'Camila', 'Mateo', 'Paula', 'Andres', 'Gabriela'
];
const lastNames = [
  // English
  'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Miller', 'Davis', 'Martinez', 'Moore', 'Taylor',
  // Spanish (no accents)
  'Garcia', 'Rodriguez', 'Martinez', 'Lopez', 'Gonzalez', 'Perez', 'Sanchez', 'Ramirez', 'Torres', 'Flores',
  'Rivera', 'Gomez', 'Diaz', 'Cruz', 'Morales', 'Ortiz', 'Vargas', 'Castillo', 'Jimenez', 'Ramos'
];

const generateRandomName = () => {
  return `${firstNames[Math.floor(Math.random() * firstNames.length)]} ${lastNames[Math.floor(Math.random() * lastNames.length)]}`;
};

// Expanded street names (English + Spanish)
const streets = [
  // English
  'Main St', 'Oak Ave', 'Maple Dr', 'Cedar Ln', 'Pine Rd', 'Elm St', 'Birch Way', 'Willow Ct',
  // Spanish
  'Calle Real', 'Avenida del Sol', 'Camino Verde', 'Paseo de la Reforma', 'Calle Luna', 'Avenida Libertad',
  'Camino del Río', 'Calle del Mar', 'Avenida Central', 'Calle Jardín'
];
const generateRandomAddress = () => {
  const numbers = Math.floor(Math.random() * 9999) + 1;
  return `${numbers} ${streets[Math.floor(Math.random() * streets.length)]}`;
};

// Expanded city names (English + Spanish/US-Latino)
const cities = [
  // US/English
  'New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix', 'Philadelphia', 'San Antonio', 'San Diego',
  // Spanish/Latino
  'Miami', 'El Paso', 'San Juan', 'Madrid', 'Barcelona', 'Buenos Aires', 'Monterrey', 'Guadalajara', 'Lima', 'Bogotá',
  'Caracas', 'Quito', 'Santiago', 'Valencia', 'Sevilla', 'Medellín', 'Cali', 'Puebla', 'Tijuana', 'Rosario'
];
const generateRandomCity = () => {
  return cities[Math.floor(Math.random() * cities.length)];
};

const generateRandomState = () => {
  const states = ['NY', 'CA', 'IL', 'TX', 'AZ', 'PA', 'FL', 'OH'];
  return states[Math.floor(Math.random() * states.length)];
};

const generateRandomZip = () => {
  return (Math.floor(Math.random() * 90000) + 10000).toString();
};

// Realistic tracking number generators
const generateUPSTracking = () => {
  // 1Z + 16 alphanumeric
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let tracking = '1Z';
  for (let i = 0; i < 16; i++) {
    tracking += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return tracking;
};

const generateDHLTracking = () => {
  // Randomly choose between JJD and GM formats
  if (Math.random() < 0.7) { // 70% JJD, 30% GM
    // JJD + 15-18 digits (commonly 18)
    let tracking = 'JJD';
    for (let i = 0; i < 15 + Math.floor(Math.random() * 4); i++) {
      tracking += Math.floor(Math.random() * 10);
    }
    return tracking;
  } else {
    // GM + 18 digits
    let tracking = 'GM';
    for (let i = 0; i < 18; i++) {
      tracking += Math.floor(Math.random() * 10);
    }
    return tracking;
  }
};

const generateFedExTracking = () => {
  // 12 or 15 digits
  const length = Math.random() < 0.5 ? 12 : 15;
  let tracking = '';
  for (let i = 0; i < length; i++) {
    tracking += Math.floor(Math.random() * 10);
  }
  return tracking;
};

const generateUSPSTracking = () => {
  // 20-22 digits
  const length = 20 + Math.floor(Math.random() * 3);
  let tracking = '';
  for (let i = 0; i < length; i++) {
    tracking += Math.floor(Math.random() * 10);
  }
  return tracking;
};

const generateTrackingNumberForCarrier = (carrier) => {
  switch (carrier) {
    case 'UPS':
      return generateUPSTracking();
    case 'DHL':
      return generateDHLTracking();
    case 'FedEx':
      return generateFedExTracking();
    case 'USPS':
      return generateUSPSTracking();
    default:
      return generateUPSTracking();
  }
};

function App() {
  const [formData, setFormData] = useState({
    tracking_number: '',
    carrier: '',
    shipper_name: '',
    shipper_address: '',
    shipper_city: '',
    shipper_state: '',
    shipper_zip: '',
    shipper_country: 'USA',
    consignee_name: '',
    consignee_address: '',
    consignee_city: '',
    consignee_state: '',
    consignee_zip: '',
    consignee_country: 'USA',
    weight: '',
    service_type: '',
    multipacket_count: 1,
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Base64 Gzip Compressor/Decompressor state
  const [b64Input, setB64Input] = useState('');
  const [b64Output, setB64Output] = useState('');
  const [b64Loading, setB64Loading] = useState(false);
  const [b64Error, setB64Error] = useState('');
  const b64OutputRef = useRef(null);

  // Label Preview state
  const [labelPreviewOpen, setLabelPreviewOpen] = useState(false);

  // Carrier Selection Dialog state
  const [carrierSelectionOpen, setCarrierSelectionOpen] = useState(false);

  // Realistic PDF generation hook
  const {
    isGenerating,
    labelData,
    carrier: pdfCarrier,
    generateRealisticLabelPDF,
    handlePDFGenerated,
    LabelRenderer
  } = useRealisticLabelPDF();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const generateRandomData = (type) => {
    if (type === 'shipper') {
      setFormData(prev => ({
        ...prev,
        shipper_name: generateRandomName(),
        shipper_address: generateRandomAddress(),
        shipper_city: generateRandomCity(),
        shipper_state: generateRandomState(),
        shipper_zip: generateRandomZip(),
      }));
    } else if (type === 'consignee') {
      setFormData(prev => ({
        ...prev,
        consignee_name: generateRandomName(),
        consignee_address: generateRandomAddress(),
        consignee_city: generateRandomCity(),
        consignee_state: generateRandomState(),
        consignee_zip: generateRandomZip(),
      }));
    } else if (type === 'all') {
      // Use the currently selected carrier if set, otherwise pick a random one
      const carrier = formData.carrier || carriers[Math.floor(Math.random() * carriers.length)];
      setFormData(prev => ({
        ...prev,
        tracking_number: generateTrackingNumberForCarrier(carrier),
        carrier,
        service_type: serviceTypes[Math.floor(Math.random() * serviceTypes.length)],
        weight: (Math.random() * 20 + 0.1).toFixed(2),
        multipacket_count: Math.floor(Math.random() * 3) + 1, // Random number between 1-3
        shipper_name: generateRandomName(),
        shipper_address: generateRandomAddress(),
        shipper_city: generateRandomCity(),
        shipper_state: generateRandomState(),
        shipper_zip: generateRandomZip(),
        consignee_name: generateRandomName(),
        consignee_address: generateRandomAddress(),
        consignee_city: generateRandomCity(),
        consignee_state: generateRandomState(),
        consignee_zip: generateRandomZip(),
      }));
    }
  };

  const openPdfInNewTab = (blob) => {
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank');
  };

  const handleSubmit = async (e, random = false) => {
    if (e) e.preventDefault();
    try {
      // Convert weight to number and ensure ZIP codes are strings
      const submitData = {
        ...formData,
        weight: parseFloat(formData.weight),
        shipper_zip: formData.shipper_zip.toString(),
        consignee_zip: formData.consignee_zip.toString(),
      };
      // Validate required fields
      const requiredFields = [
        'tracking_number', 'carrier', 'shipper_name', 'shipper_address',
        'shipper_city', 'shipper_state', 'shipper_zip', 'shipper_country',
        'consignee_name', 'consignee_address', 'consignee_city',
        'consignee_state', 'consignee_zip', 'consignee_country',
        'weight', 'service_type'
      ];
      const missingFields = requiredFields.filter(field => !submitData[field]);
      if (missingFields.length > 0) {
        setSnackbar({
          open: true,
          message: `Please fill in all required fields: ${missingFields.join(', ')}`,
          severity: 'error',
        });
        return;
      }
      if (isNaN(submitData.weight) || submitData.weight <= 0) {
        setSnackbar({
          open: true,
          message: 'Weight must be a positive number',
          severity: 'error',
        });
        return;
      }
      const response = await axios.post('http://localhost:8000/generate-label', submitData, {
        responseType: 'blob',
      });
      // Show PDF in new tab
      openPdfInNewTab(new Blob([response.data], { type: 'application/pdf' }));
      setSnackbar({
        open: true,
        message: 'Shipping label generated and opened in new tab!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error details:', error.response?.data || error.message);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Error generating shipping label. Please try again.',
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handlePrintRandomLabel = async () => {
    const carrier = carriers[Math.floor(Math.random() * carriers.length)];
    const randomData = {
      tracking_number: generateTrackingNumberForCarrier(carrier),
      carrier,
      service_type: serviceTypes[Math.floor(Math.random() * serviceTypes.length)],
      weight: (Math.random() * 20 + 0.1).toFixed(2),
      multipacket_count: 1,
      shipper_name: generateRandomName(),
      shipper_address: generateRandomAddress(),
      shipper_city: generateRandomCity(),
      shipper_state: generateRandomState(),
      shipper_zip: generateRandomZip(),
      shipper_country: 'USA',
      consignee_name: generateRandomName(),
      consignee_address: generateRandomAddress(),
      consignee_city: generateRandomCity(),
      consignee_state: generateRandomState(),
      consignee_zip: generateRandomZip(),
      consignee_country: 'USA',
    };
    try {
      const submitData = {
        ...randomData,
        weight: parseFloat(randomData.weight),
        shipper_zip: randomData.shipper_zip.toString(),
        consignee_zip: randomData.consignee_zip.toString(),
      };
      const response = await axios.post('http://localhost:8000/generate-label', submitData, {
        responseType: 'blob',
      });
      openPdfInNewTab(new Blob([response.data], { type: 'application/pdf' }));
      setSnackbar({
        open: true,
        message: 'Random shipping label generated and opened in new tab!',
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Error generating shipping label. Please try again.',
        severity: 'error',
      });
    }
  };

  const handlePrintMultipacketLabel = async () => {
    const carrier = carriers[Math.floor(Math.random() * carriers.length)];
    const N = Math.floor(Math.random() * 4) + 2; // 2-5 packets
    const randomData = {
      tracking_number: generateTrackingNumberForCarrier(carrier),
      carrier,
      service_type: serviceTypes[Math.floor(Math.random() * serviceTypes.length)],
      weight: (Math.random() * 20 + 0.1).toFixed(2),
      multipacket_count: N,
      shipper_name: generateRandomName(),
      shipper_address: generateRandomAddress(),
      shipper_city: generateRandomCity(),
      shipper_state: generateRandomState(),
      shipper_zip: generateRandomZip(),
      shipper_country: 'USA',
      consignee_name: generateRandomName(),
      consignee_address: generateRandomAddress(),
      consignee_city: generateRandomCity(),
      consignee_state: generateRandomState(),
      consignee_zip: generateRandomZip(),
      consignee_country: 'USA',
    };
    try {
      const submitData = {
        ...randomData,
        weight: parseFloat(randomData.weight),
        shipper_zip: randomData.shipper_zip.toString(),
        consignee_zip: randomData.consignee_zip.toString(),
      };
      const response = await axios.post('http://localhost:8000/generate-label', submitData, {
        responseType: 'blob',
      });
      openPdfInNewTab(new Blob([response.data], { type: 'application/pdf' }));
      setSnackbar({
        open: true,
        message: `Multipacket label (${N} packets) generated and opened in new tab!`,
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Error generating multipacket label. Please try again.',
        severity: 'error',
      });
    }
  };

  const handlePrintRealisticRandomLabel = () => {
    setCarrierSelectionOpen(true);
  };

  const handleCarrierSelected = (selectedCarrier) => {
    setCarrierSelectionOpen(false);
    
    const randomData = {
      tracking_number: generateTrackingNumberForCarrier(selectedCarrier),
      carrier: selectedCarrier,
      service_type: serviceTypes[Math.floor(Math.random() * serviceTypes.length)],
      weight: (Math.random() * 20 + 0.1).toFixed(2),
      multipacket_count: 1,
      shipper_name: generateRandomName(),
      shipper_address: generateRandomAddress(),
      shipper_city: generateRandomCity(),
      shipper_state: generateRandomState(),
      shipper_zip: generateRandomZip(),
      shipper_country: 'USA',
      consignee_name: generateRandomName(),
      consignee_address: generateRandomAddress(),
      consignee_city: generateRandomCity(),
      consignee_state: generateRandomState(),
      consignee_zip: generateRandomZip(),
      consignee_country: 'USA',
    };

    try {
      generateRealisticLabelPDF(randomData, selectedCarrier);
      setSnackbar({
        open: true,
        message: `Generating realistic ${selectedCarrier} label PDF...`,
        severity: 'info',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error generating realistic label PDF. Please try again.',
        severity: 'error',
      });
    }
  };

  const handleCloseCarrierSelection = () => {
    setCarrierSelectionOpen(false);
  };

  // Handle PDF generation completion
  React.useEffect(() => {
    if (!isGenerating && labelData) {
      const result = handlePDFGenerated(true);
      if (result.success) {
        setSnackbar({
          open: true,
          message: 'Realistic shipping label PDF generated and opened in new tab!',
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: `Error generating PDF: ${result.error}`,
          severity: 'error',
        });
      }
    }
  }, [isGenerating, labelData]);

  const handleCompress = async () => {
    setB64Loading(true);
    setB64Error('');
    setB64Output('');
    try {
      const res = await axios.post('http://localhost:8000/compress-base64-gzip', { data: b64Input });
      setB64Output(res.data.result);
    } catch (e) {
      setB64Error(e.response?.data?.error || 'Compression failed');
    } finally {
      setB64Loading(false);
    }
  };

  const handleDecompress = async () => {
    setB64Loading(true);
    setB64Error('');
    setB64Output('');
    try {
      const res = await axios.post('http://localhost:8000/decompress-base64-gzip', { data: b64Input });
      setB64Output(res.data.result);
    } catch (e) {
      setB64Error(e.response?.data?.error || 'Decompression failed');
    } finally {
      setB64Loading(false);
    }
  };

  const handleCopyB64Output = () => {
    if (b64OutputRef.current) {
      navigator.clipboard.writeText(b64OutputRef.current.value);
      setSnackbar({ open: true, message: 'Output copied to clipboard!', severity: 'success' });
    }
  };

  const handleOpenLabelPreview = () => {
    setLabelPreviewOpen(true);
  };

  const handleCloseLabelPreview = () => {
    setLabelPreviewOpen(false);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Shipping Label Generator
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handlePrintRandomLabel}
          >
            Print Random Label
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handlePrintMultipacketLabel}
          >
            Print Multipacket Label
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<PictureAsPdfIcon />}
            onClick={handlePrintRealisticRandomLabel}
            disabled={isGenerating}
          >
            {isGenerating ? 'Generating...' : 'Print Realistic Label (Choose Carrier)'}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<PreviewIcon />}
            onClick={handleOpenLabelPreview}
          >
            Preview Realistic Labels
          </Button>
        </Box>
        
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Carrier Information
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Carrier"
                name="carrier"
                value={formData.carrier}
                onChange={handleChange}
                required
              >
                {carriers.map((carrier) => (
                  <MenuItem key={carrier} value={carrier}>
                    {carrier}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tracking Number"
                name="tracking_number"
                value={formData.tracking_number}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                Shipper Information
                <Tooltip title="Generate random shipper data">
                  <IconButton onClick={() => generateRandomData('shipper')} size="small" sx={{ ml: 1 }}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                name="shipper_name"
                value={formData.shipper_name}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                name="shipper_address"
                value={formData.shipper_address}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="City"
                name="shipper_city"
                value={formData.shipper_city}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="State"
                name="shipper_state"
                value={formData.shipper_state}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="ZIP Code"
                name="shipper_zip"
                value={formData.shipper_zip}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                Consignee Information
                <Tooltip title="Generate random consignee data">
                  <IconButton onClick={() => generateRandomData('consignee')} size="small" sx={{ ml: 1 }}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                name="consignee_name"
                value={formData.consignee_name}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                name="consignee_address"
                value={formData.consignee_address}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="City"
                name="consignee_city"
                value={formData.consignee_city}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="State"
                name="consignee_state"
                value={formData.consignee_state}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="ZIP Code"
                name="consignee_zip"
                value={formData.consignee_zip}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Package Information
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Weight (kg)"
                name="weight"
                type="number"
                value={formData.weight}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Service Type"
                name="service_type"
                value={formData.service_type}
                onChange={handleChange}
                required
              >
                {serviceTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                >
                  Generate Shipping Label
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Base64 Gzip Compressor/Decompressor Section */}
      <Paper elevation={2} sx={{ p: 3, mt: 6, mb: 4 }}>
        <Typography variant="h5" gutterBottom>Base64 Gzip Compressor / Decompressor</Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Enter text to compress or a base64 gzip string to decompress. Useful for encoding/decoding payloads for APIs, QR codes, etc.
        </Typography>
        <TextField
          label="Input"
          multiline
          minRows={3}
          maxRows={8}
          fullWidth
          value={b64Input}
          onChange={e => setB64Input(e.target.value)}
          sx={{ my: 2 }}
        />
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Button variant="contained" color="primary" onClick={handleCompress} disabled={b64Loading}>
            Compress
          </Button>
          <Button variant="contained" color="secondary" onClick={handleDecompress} disabled={b64Loading}>
            Decompress
          </Button>
        </Box>
        {b64Loading && <Typography color="info.main">Processing...</Typography>}
        {b64Error && <Typography color="error.main">{b64Error}</Typography>}
        <TextField
          label="Output"
          multiline
          minRows={3}
          maxRows={8}
          fullWidth
          inputRef={b64OutputRef}
          value={b64Output}
          sx={{ my: 2 }}
          InputProps={{ readOnly: true }}
        />
        <Button variant="outlined" onClick={handleCopyB64Output} disabled={!b64Output}>
          Copy Output
        </Button>
      </Paper>

      {/* Realistic Label Preview Dialog */}
      <LabelPreview 
        open={labelPreviewOpen}
        onClose={handleCloseLabelPreview}
        formData={formData}
      />

      {/* Carrier Selection Dialog */}
      <CarrierSelectionDialog
        open={carrierSelectionOpen}
        onClose={handleCloseCarrierSelection}
        onCarrierSelected={handleCarrierSelected}
        isGenerating={isGenerating}
      />

      {/* Hidden Label Renderer for PDF Generation */}
      {LabelRenderer}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Box sx={{ mt: 6, textAlign: 'center', color: 'gray', fontSize: 14 }}>
        <strong>Wilkins:</strong> This shipping label generation is intended as is to generate shipping labels that can be used for testing. It is not intended to be used in production and it comes without warranty.
      </Box>
    </Container>
  );
}

export default App; 